import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TableCompData, TabsComp } from "../../../../components";
import EmptyTable from "../../../../components/Common/EmptyTable";
import TableSkeletonLoader from "../../../../components/Common/TableSkeletonLoader";
import {
  KycReviewStatusBadge,
  ParticipantTypeBadge,
} from "../../../../components/Admin/InvestorPortal/AdminStatusBadge";
import KycMatchIndicator from "../../../../components/Admin/InvestorPortal/KycMatchIndicator";
import InvestorPortalPageHeader from "../../../../components/Admin/InvestorPortal/InvestorPortalPageHeader";
import InvestorPortalTableSection, {
  TableRowLink,
  useClientPagination,
} from "../../../../components/Admin/InvestorPortal/InvestorPortalTableSection";
import TableActionDropdown from "../../../../components/Admin/InvestorPortal/TableActionDropdown";
import { useListStaffInvestorKycQuery } from "../../../../store/admin/investorKyc/staffInvestorKycApi";
import { KYC_REVIEW_STATUS, KYC_MATCH_RESULT } from "../../../../store/admin/investorOperations/constants";
import { mapStaffKycListItem } from "../../../../utils/staffKycAdapter";
import moment from "moment";
import { withInvestorPortalGuard } from "../withInvestorPortalGuard";

const PER_PAGE = 10;

function filterRows(rows, { hasMismatch, assignedReviewer }) {
  return rows.filter((s) => {
    if (hasMismatch === "yes" && ![KYC_MATCH_RESULT.MISMATCH, KYC_MATCH_RESULT.PARTIAL_MATCH].includes(s.matchResult)) {
      return false;
    }
    if (hasMismatch === "no" && [KYC_MATCH_RESULT.MISMATCH, KYC_MATCH_RESULT.PARTIAL_MATCH].includes(s.matchResult)) {
      return false;
    }
    if (assignedReviewer === "me" && s.assignedReviewer !== "Staff Admin") return false;
    if (assignedReviewer === "unassigned" && s.assignedReviewer) return false;
    return true;
  });
}

function KycQueueTable({ statusFilter, search, accountType, hasMismatch, assignedReviewer, applicantFilter }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const apiQuery = useListStaffInvestorKycQuery({
    status: statusFilter || undefined,
    account_type:
      accountType === "standalone_investor"
        ? "investor"
        : accountType === "cooperative_member"
          ? "member"
          : undefined,
    search: search || undefined,
    applicant_id: applicantFilter || undefined,
    page,
    per_page: PER_PAGE,
  });

  const all = useMemo(
    () => (apiQuery.data?.items || []).map(mapStaffKycListItem).filter(Boolean),
    [apiQuery.data]
  );

  const filtered = useMemo(
    () => filterRows(all, { hasMismatch, assignedReviewer }),
    [all, hasMismatch, assignedReviewer]
  );

  const apiPagination = apiQuery.data?.pagination || {};
  const { pageData, pagination } = useClientPagination(filtered, {
    page,
    perPage: PER_PAGE,
    setPage,
    total: apiPagination.total ?? filtered.length,
  });

  const columns = [
    {
      name: "Applicant",
      cell: (row) => (
        <TableRowLink onClick={() => navigate(`/super_admin/investor_portal/investors/kyc/${row.id}`)}>
          {row.applicantName}
        </TableRowLink>
      ),
      minWidth: "150px",
      grow: 1,
    },
    { name: "Account type", cell: (r) => <ParticipantTypeBadge type={r.accountType} />, minWidth: "150px" },
    { name: "NIN", selector: (r) => r.ninMasked, minWidth: "110px" },
    { name: "Submitted", selector: (r) => moment(r.submittedAt).format("DD-MMM-YYYY"), minWidth: "115px" },
    { name: "Status", cell: (r) => <KycReviewStatusBadge status={r.status} />, minWidth: "130px" },
    { name: "Match", cell: (r) => <KycMatchIndicator matchResult={r.matchResult} />, minWidth: "120px" },
    { name: "Reviewer", selector: (r) => r.assignedReviewer || "Unassigned", minWidth: "120px" },
    { name: "Updated", selector: (r) => moment(r.reviewedAt || r.submittedAt).format("DD-MMM-YY"), minWidth: "100px" },
    {
      name: "Actions",
      cell: (r) => (
        <TableActionDropdown
          items={[{ name: "Review", action: () => navigate(`/super_admin/investor_portal/investors/kyc/${r.id}`) }]}
          row={r}
        />
      ),
      width: "80px",
      center: true,
    },
  ];

  return (
    <InvestorPortalTableSection pagination={pagination} data-tour="staff-invest-kyc-actions">
      <TableCompData
        columns={columns}
        data={pageData}
        progressPending={apiQuery.isFetching}
        progressComponent={<TableSkeletonLoader rows={8} columns={9} />}
        noDataComponent={<EmptyTable message="No KYC submissions in this queue." />}
      />
    </InvestorPortalTableSection>
  );
}

function KycReviewsListPage() {
  const [searchParams] = useSearchParams();
  const applicantFilter = searchParams.get("applicant") || "";
  const [search, setSearch] = useState("");
  const [accountType, setAccountType] = useState("");
  const [hasMismatch, setHasMismatch] = useState("");
  const [assignedReviewer, setAssignedReviewer] = useState("");

  const filters = { search, accountType, hasMismatch, assignedReviewer, applicantFilter };

  const useTab = [
    { name: "All", component: <KycQueueTable {...filters} statusFilter="" /> },
    { name: "Pending", component: <KycQueueTable {...filters} statusFilter={KYC_REVIEW_STATUS.PENDING} /> },
    { name: "Under Review", component: <KycQueueTable {...filters} statusFilter={KYC_REVIEW_STATUS.UNDER_REVIEW} /> },
    { name: "Approved", component: <KycQueueTable {...filters} statusFilter={KYC_REVIEW_STATUS.APPROVED} /> },
    { name: "Rejected", component: <KycQueueTable {...filters} statusFilter={KYC_REVIEW_STATUS.REJECTED} /> },
    {
      name: "Resubmission Required",
      component: <KycQueueTable {...filters} statusFilter={KYC_REVIEW_STATUS.RESUBMISSION_REQUIRED} />,
    },
  ];

  return (
    <div className="pb-4">
      <InvestorPortalPageHeader
        title="KYC Reviews"
        subtitle="Review standalone investor and cooperative member KYC submissions. Full NIN is never displayed."
      />

      <div className="row g-2 mb-3" data-tour="staff-invest-kyc-queue">
        <div className="col-md-3">
          <input className="form-control form-control-sm" placeholder="Search applicant" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="col-md-2">
          <select className="form-select form-select-sm" value={accountType} onChange={(e) => setAccountType(e.target.value)}>
            <option value="">All account types</option>
            <option value="standalone_investor">Standalone investor</option>
            <option value="cooperative_member">Cooperative member</option>
          </select>
        </div>
        <div className="col-md-2">
          <select className="form-select form-select-sm" value={hasMismatch} onChange={(e) => setHasMismatch(e.target.value)}>
            <option value="">Match result</option>
            <option value="yes">Has mismatch</option>
            <option value="no">No mismatch</option>
          </select>
        </div>
        <div className="col-md-2">
          <select className="form-select form-select-sm" value={assignedReviewer} onChange={(e) => setAssignedReviewer(e.target.value)}>
            <option value="">All reviewers</option>
            <option value="me">Assigned to me</option>
            <option value="unassigned">Unassigned</option>
          </select>
        </div>
      </div>

      <TabsComp TabArray={useTab} input="name" justifyContent="justify-content-start" tabClassName="px-0" TabArrayClassName="px-1" />
    </div>
  );
}

export default withInvestorPortalGuard(KycReviewsListPage, "investorPortal");
